import {
  Text,
  Heading,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  useSlotRecipe,
  Box,
  HStack,
} from "shared/components/Spor";

import { createContext, ReactElement, ReactNode, useContext } from "react";

import { InformationOutline18Icon } from "@vygruppen/spor-icon-react";
import { useTheme } from "styled-components";
import { moduleRecipe } from "shared/theme/recipes/module";

export type ModuleSize = "md" | "lg";

export type ModuleProps = {
  title: ReactNode;
  titleIcon?: ReactNode; /** Icon displayed to the left of the title */
  specification?: ReactNode;
  badgeLeft1?: ReactElement; /** Badge component shown after the title */
  badgeLeft2?: ReactElement; /** Second badge component shown after the title */
  action1?: ReactNode; /** First action button on the right side of the header */
  action2?: ReactNode; /** Second action button on the right side of the header */
  badgeRight?: ReactElement; /** Badge component on the right side of the header */
  infoButtonText?: ReactNode; /** Show information popover button with this text on the upper right corner */
  size?: ModuleSize; /** Controls padding, font size and background. Defaults to "md" */
  disablePadding?: boolean; /** Removes left and right padding on header and body */
  scrollable?: boolean;
  children?: ReactNode;
  gridArea?: string;
  className?: string;
};

const ModuleDepthContext = createContext(0);

export const Module = ({
  title,
  titleIcon,
  specification,
  badgeLeft1,
  badgeLeft2,
  action1,
  action2,
  badgeRight,
  infoButtonText,
  size = "md",
  disablePadding = false,
  scrollable = false,
  children,
  gridArea,
  className,
}: ModuleProps) => {
  const depth = useContext(ModuleDepthContext);
  const theme = useTheme();
  const recipe = useSlotRecipe({ recipe: moduleRecipe });
  const styles = recipe({ size, disablePadding });

  const showInfoButton = Boolean(infoButtonText) && size === "md";
  const lgModuleNoScrollPadding =
    size === "lg" && !scrollable && !disablePadding;
  const hasRightContent = action1 || action2 || badgeRight || showInfoButton;

  const specificationFont = {
    fontWeight: "400",
    fontSize: size === "lg" ? "24px" : "18px",
    lineHeight: size === "lg" ? "36px" : "27px",
  } as const;

  const childDepth = depth + 1;

  return (
    <ModuleDepthContext.Provider value={childDepth}>
      <Box
        gridArea={gridArea}
        className={className}
        css={{
          ...styles.root,
          borderColor: theme.colorOutlineSecondary,
          overscrollBehavior: depth === 0 && scrollable ? "none" : undefined,
        }}
        overflow={scrollable ? undefined : "clip"}
        overflowY={scrollable ? "auto" : undefined}
        data-group
      >
        <Box
          css={{
            ...styles.header,
            zIndex: 100 - depth,
            paddingBottom: "0",
          }}
        >
          <HStack
            gap="1"
            alignItems="center"
            flexWrap="wrap"
            flexGrow="1"
            flexShrink="1"
            flexBasis="min-content"
          >
            <HStack
              gap="1"
              alignItems="center"
              flexWrap="nowrap"
              flexShrink="1"
              minWidth="0"
            >
              {titleIcon}
              <Box
                display="flex"
                flexWrap="wrap"
                alignItems="baseline"
                columnGap="1"
                minWidth="0"
              >
                <Heading as="h3" variant={size}>
                  {title}
                </Heading>
                {specification && (
                  <Box
                    as="span"
                    display="inline-flex"
                    alignItems="baseline"
                    whiteSpace="nowrap"
                  >
                    <Text as="span" aria-hidden mr="1" {...specificationFont}>
                      |
                    </Text>
                    <Text as="span" {...specificationFont}>
                      {specification}
                    </Text>
                  </Box>
                )}
              </Box>
            </HStack>
            {badgeLeft1}
            {badgeLeft2}
          </HStack>
          {hasRightContent && (
            <HStack
              gap="1"
              alignItems="center"
              alignSelf="flex-end"
              flexShrink="0"
              marginLeft="auto"
            >
              {action1}
              {action2}
              {badgeRight}
              {showInfoButton && (
                <Popover>
                  <PopoverTrigger>
                    <IconButton
                      variant="ghost"
                      size="xs"
                      icon={<InformationOutline18Icon />}
                      aria-label="Informasjon"
                    />
                  </PopoverTrigger>

                  <PopoverContent>{infoButtonText}</PopoverContent>
                </Popover>
              )}
            </HStack>
          )}
        </Box>
        <Box
          css={{
            ...styles.body,
            ...(lgModuleNoScrollPadding && { paddingBottom: 0 }),
          }}
        >
          {children}
        </Box>
      </Box>
    </ModuleDepthContext.Provider>
  );
};
